/**
 * freeFormService.js — Bölüm 25-C/D/E
 * Free Form (Esnek Form Builder) için servis fonksiyonları.
 * Base URL: /api/v1/free-form
 */
import axios from "axios";
import { JAVA_BACKEND_API_URL, getAuthHeader } from "../Components/Contexts/BackendConstants";

const BASE = `${JAVA_BACKEND_API_URL}/free-form`;

// ─── Helpers ────────────────────────────────────────────────────────────────

function headers() {
  return { headers: getAuthHeader() };
}

/**
 * getTemplateTree() yanıtındaki nested node ağacını düz diziye çevirir.
 * Backend "rootNodes" key'i kullanıyor (API Guide'da "nodes" yazıyordu — gerçek key farklı).
 * Fallback zinciri: rootNodes → nodes → []
 * parentId referansları korunur, children sıyrılır.
 *
 * @param {object} tree - getTemplateTree() yanıtı
 * @returns {Array} - Düz node listesi
 */
export function flatFreeFormNodes(tree) {
  const result = [];
  const rawList = tree?.rootNodes ?? tree?.nodes ?? [];
  // parentId parametresi: backend nested tree'de child node'lara parentId koymayabilir,
  // walk sırasında parent'ın id'si inject edilir.
  (function walk(list, parentId = null) {
    list.forEach(({ children = [], ...node }) => {
      // Backend parentId vermemişse traversal'dan inject et
      const finalNode =
        parentId != null && (node.parentId == null || node.parentId === undefined)
          ? { ...node, parentId }
          : node;
      result.push(finalNode);
      const nodeId = node._id || node.id;
      if (children.length) walk(children, nodeId);
    });
  })(rawList, null);
  return result;
}

// ─── Templates ──────────────────────────────────────────────────────────────

/**
 * Şablon oluştur
 * Body: { name, description, targetEntityType, layers: [{order, name, type}] }
 * Response: { result, message, data: <newId> }
 */
export async function createTemplate(body) {
  const res = await axios.post(`${BASE}/templates`, body, headers());
  return res.data;
}

/**
 * Şablon listesi (paginated)
 * Params: { targetEntityType?, page?, limit? }
 * Response içinde submissionCount dahil.
 */
export async function listTemplates(params = {}) {
  const res = await axios.get(`${BASE}/templates`, { ...headers(), params });
  return res.data;
}

/**
 * Tek şablon metadata (node'suz)
 */
export async function getTemplate(id) {
  const res = await axios.get(`${BASE}/templates/${id}`, headers());
  return res.data;
}

/**
 * Şablon + tam node ağacı
 * Form doldurma ve önizleme için kullan.
 */
export async function getTemplateTree(id) {
  const res = await axios.get(`${BASE}/templates/${id}/tree`, headers());
  console.log('[freeFormService] getTemplateTree raw res.data:', JSON.stringify(res.data).substring(0, 500));
  return res.data;
}

/**
 * Şablon güncelle (409 → locked, submission varsa)
 * Body: { name?, description?, targetEntityType? }
 */
export async function updateTemplate(id, body) {
  const res = await axios.put(`${BASE}/templates/${id}`, body, headers());
  return res.data;
}

/**
 * Şablon sil
 * 409 → locked (submission var)
 * 204 → başarı
 */
export async function deleteTemplate(id) {
  const res = await axios.delete(`${BASE}/templates/${id}`, headers());
  return res.data;
}

// ─── Nodes ──────────────────────────────────────────────────────────────────

/**
 * Node ekle
 * Body (CATEGORY): { layerOrder, name }
 * Body (QUESTION):  { layerOrder, name, parentId, answerType, choices?, required? }
 * answerType: "TEXT" | "NUMBER" | "SINGLE_CHOICE" | "MULTI_CHOICE" | "PHOTO"
 * 409 → locked
 */
export async function addNode(templateId, body) {
  const res = await axios.post(`${BASE}/templates/${templateId}/nodes`, body, headers());
  return res.data;
}

/**
 * Node güncelle
 * Body: partial — sadece gönderilen key'ler güncellenir
 * 409 → locked
 */
export async function updateNode(nodeId, body) {
  const res = await axios.put(`${BASE}/nodes/${nodeId}`, body, headers());
  return res.data;
}

/**
 * Node + tüm alt node'ları sil
 * 409 → locked
 * 204 → başarı
 */
export async function deleteNode(nodeId) {
  const res = await axios.delete(`${BASE}/nodes/${nodeId}`, headers());
  return res.data;
}

// ─── Submissions ────────────────────────────────────────────────────────────

/**
 * Form doldur/gönder
 * Body: { fuarId, targetEntityType, targetEntityId, answers: [{questionNodeId, answerType, ...}] }
 * PHOTO sorular answers listesine dahil edilmez.
 */
export async function submitForm(templateId, body) {
  const res = await axios.post(`${BASE}/templates/${templateId}/submissions`, body, headers());
  return res.data;
}

/**
 * Submission listesi
 * Params: { templateId?, fuarId?, targetEntityId?, from?, to?, page?, limit? }
 */
export async function listSubmissions(params = {}) {
  const res = await axios.get(`${BASE}/submissions`, { ...headers(), params });
  return res.data;
}

/**
 * Tekil submission (tüm cevaplar dahil)
 */
export async function getSubmission(id) {
  const res = await axios.get(`${BASE}/submissions/${id}`, headers());
  return res.data;
}

/**
 * Submission sil
 * 204 → başarı
 */
export async function deleteSubmission(id) {
  const res = await axios.delete(`${BASE}/submissions/${id}`, headers());
  return res.data;
}

// ─── Photos ─────────────────────────────────────────────────────────────────

/**
 * Fotoğraf yükle (multipart)
 * form-data alanları: file, questionNodeId
 */
export async function uploadFreeFormPhoto(submissionId, file, questionNodeId) {
  const form = new FormData();
  form.append("file", file);
  form.append("questionNodeId", questionNodeId);
  const res = await axios.post(
    `${BASE}/submissions/${submissionId}/photo`,
    form,
    { headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" } }
  );
  return res.data;
}

/**
 * Submission fotoğraflarını getir (isteğe bağlı questionNodeId filtresi)
 */
export async function getFreeFormPhotos(submissionId, questionNodeId = null) {
  const params = questionNodeId ? { questionNodeId } : {};
  const res = await axios.get(
    `${BASE}/submissions/${submissionId}/photos`,
    { ...headers(), params }
  );
  return res.data;
}

/**
 * Fotoğraf tam URL'i
 * storedFileName → /public/files/free_form_photos/{storedFileName}
 */
export function getFreeFormPhotoUrl(storedFileName) {
  if (!storedFileName) return null;
  const base = JAVA_BACKEND_API_URL.replace("/api/v1", "");
  return `${base}/public/files/free_form_photos/${storedFileName}`;
}

